<template>
  <BasePopup
    :show="show"
    title="Verifica la tua email"
    close-test-id="email-code-close"
    :close-on-backdrop="false"
    card-class="w-full md:w-[480px] lg:p-8 lg:pt-6 p-6 pt-4 flex flex-col nap-login-screen"
    @close="close"
  >
    <form class="space-y-4" data-testid="email-code-form" @submit.prevent="verify">
      <p class="text-sm text-black leading-5">
        Abbiamo inviato un codice di 6 cifre a
        <strong class="text-black break-all">{{ email }}</strong>. Inseriscilo qui sotto per continuare.
      </p>

      <OtpCodeBoxes
        ref="boxes"
        :code="shownCode"
        :length="codeLength"
        :state="boxState"
        data-testid="email-code-input"
        aria-label="Codice di verifica"
        :value="code"
        :disabled="verifying || busy || boxState === 'success'"
        @input="onInput"
        @paste.prevent="onPaste"
      />

      <p v-if="verifying" data-testid="email-code-verifying" class="text-center text-gray-500 text-sm">
        Verifica in corso...
      </p>
      <p v-else-if="busy && busyText" data-testid="email-code-busy" class="text-center text-gray-500 text-sm">
        {{ busyText }}
      </p>
      <p v-else-if="boxState === 'success'" data-testid="email-code-success" class="text-center text-primary text-sm font-semibold">
        Email verificata
      </p>
      <p v-else-if="errorMessage" data-testid="email-code-error" class="text-center text-red-500 text-sm">
        {{ errorMessage }}
      </p>
      <p v-else-if="infoMessage" data-testid="email-code-info" class="text-center text-gray-500 text-sm">
        {{ infoMessage }}
      </p>

      <div class="flex items-center justify-between text-sm text-gray-500">
        <button type="button" data-testid="email-code-change" class="underline hover:text-primary" @click="close">
          Cambia email
        </button>
        <button
          type="button"
          data-testid="email-code-resend"
          class="underline hover:text-primary disabled:no-underline disabled:opacity-60"
          :disabled="sending || secondsLeft > 0 || boxState === 'success'"
          @click="send"
        >
          {{ secondsLeft > 0 ? `Invia di nuovo tra ${secondsLeft} s` : "Invia di nuovo" }}
        </button>
      </div>
    </form>
  </BasePopup>
</template>

<script>
// Email code step of the website sign-up (#666). Opening it sends the code;
// a correct code emits `verified` with the single-use proof the account
// creation hands to the server (#766). "Cambia email" and × emit `close`.
// `busy` keeps it open, locked, while the parent creates the account.
// There is no submit button: the sixth digit verifies, and a right code
// plays the boxes' success animation before `verified`.
import BasePopup from "@/components/BasePopup.vue";
import OtpCodeBoxes, { OTP_SUCCESS_MS } from "@/components/OtpCodeBoxes.vue";
import {
  EMAIL_CODE_LENGTH,
  EMAIL_CODE_RESENT_MESSAGE,
  EMAIL_CODE_RESEND_SECONDS,
  sanitizeEmailCode,
  sendEmailCode,
  verifyEmailCode,
} from "@/services/emailVerification.js";

export default {
  name: "EmailCodePopup",
  components: { BasePopup, OtpCodeBoxes },
  props: {
    show: { type: Boolean, default: false },
    email: { type: String, default: "" },
    brand: { type: String, default: "" },
    busy: { type: Boolean, default: false },
    busyText: { type: String, default: "" },
    // How long the success animation plays before `verified` (0 in tests).
    successMs: { type: Number, default: OTP_SUCCESS_MS },
  },
  emits: ["verified", "close"],
  data() {
    return {
      code: "",
      codeLength: EMAIL_CODE_LENGTH,
      sending: false,
      verifying: false,
      secondsLeft: 0,
      errorMessage: "",
      infoMessage: "",
      timer: null,
      boxState: "",
      failedCode: "",
      errorTimer: null,
      // Whether a code already went out in this opening: the next send is a resend.
      sentOnce: false,
    };
  },
  computed: {
    // A wrong code leaves the field at once; the boxes keep it while they shake.
    shownCode() {
      return this.boxState === "error" ? this.failedCode : this.code;
    },
  },
  watch: {
    show: {
      immediate: true,
      handler(open) {
        if (open) this.start();
        else this.stopTimer();
      },
    },
  },
  beforeUnmount() {
    this.stopTimer();
    clearTimeout(this.errorTimer);
  },
  methods: {
    start() {
      this.code = "";
      this.failedCode = "";
      this.sentOnce = false;
      this.errorMessage = "";
      this.infoMessage = "";
      // A send left in flight by a previous open (the user corrected the
      // email) must not block this one.
      this.sending = false;
      this.verifying = false;
      this.setBoxState("");
      this.send();
      this.$nextTick(() => this.$refs.boxes && this.$refs.boxes.focus());
    },
    async send() {
      if (this.sending) return;
      this.sending = true;
      this.errorMessage = "";
      this.infoMessage = "Invio del codice in corso...";
      const result = await sendEmailCode(this.email, this.brand);
      this.sending = false;
      if (result.ok) {
        this.code = "";
        this.failedCode = "";
        this.setBoxState("");
        this.infoMessage = this.sentOnce
          ? EMAIL_CODE_RESENT_MESSAGE
          : "Codice inviato. Controlla la tua casella email.";
        this.sentOnce = true;
      } else {
        this.infoMessage = "";
        this.errorMessage = result.message;
      }
      // Also after a failure: the server enforces the same cooldown.
      this.startTimer();
    },
    startTimer() {
      this.stopTimer();
      this.secondsLeft = EMAIL_CODE_RESEND_SECONDS;
      this.timer = setInterval(() => {
        this.secondsLeft = Math.max(0, this.secondsLeft - 1);
        if (this.secondsLeft === 0) this.stopTimer();
      }, 1000);
    },
    setBoxState(state) {
      clearTimeout(this.errorTimer);
      this.boxState = state;
      if (state === "error") this.errorTimer = setTimeout(() => (this.boxState = ""), 800);
    },
    stopTimer() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    },
    // Copiato dall'email il codice porta spesso con sé a capo o tabulazioni (sta in
    // una cella di tabella): niente maxlength, che lo troncava prima di onInput,
    // e l'incolla passa da qui tenendo solo le cifre (#767).
    onPaste(event) {
      event.target.value = event.clipboardData ? event.clipboardData.getData("text") : "";
      this.onInput(event);
    },
    onInput(event) {
      this.code = sanitizeEmailCode(event.target.value);
      event.target.value = this.code;
      this.errorMessage = "";
      if (this.boxState === "error") this.setBoxState("");
      if (this.code.length === EMAIL_CODE_LENGTH) this.verify();
    },
    async verify() {
      if (this.verifying || this.busy || this.boxState === "success" || this.code.length !== EMAIL_CODE_LENGTH) return;
      this.verifying = true;
      this.setBoxState("verifying");
      this.errorMessage = "";
      this.infoMessage = "";
      const result = await verifyEmailCode(this.email, this.code);
      this.verifying = false;
      if (result.ok) {
        this.stopTimer();
        this.setBoxState("success");
        if (this.successMs > 0) await new Promise((r) => setTimeout(r, this.successMs));
        this.$emit("verified", { proof: result.proof, uid: result.uid });
        return;
      }
      this.errorMessage = result.message;
      this.failedCode = this.code;
      this.code = "";
      this.setBoxState("error");
      this.$nextTick(() => this.$refs.boxes && this.$refs.boxes.focus());
    },
    close() {
      if (this.verifying || this.busy || this.boxState === "success") return;
      this.stopTimer();
      this.$emit("close");
    },
  },
};
</script>
